import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

// Keep the "/" wherever the user typed it, so a 2-digit systolic ("98/60") is
// typeable, and auto-insert one after a 4th digit for users who type "12080".
// Mirrors nurse-dashboard/views/PatientTabs.js: react-input-mask's "999/999"
// rejected a "/" typed at a digit position, forcing a padded "098/60".
const formatBpWhileTyping = (input) => {
  const raw = String(input || "");
  const slash = raw.indexOf("/");
  if (slash >= 0) {
    const sys = raw.slice(0, slash).replace(/\D/g, "").slice(0, 3);
    const dia = raw.slice(slash + 1).replace(/\D/g, "").slice(0, 3);
    return `${sys}/${dia}`;
  }
  const digits = raw.replace(/\D/g, "").slice(0, 6);
  return digits.length > 3 ? `${digits.slice(0, 3)}/${digits.slice(3)}` : digits;
};

const WellmessVitals = ({
  patientId,
  appointmentId,
  handleClose,
  patientDetails,
  appointmentDate,
}) => {
  console.log(
    patientId,
    appointmentId,
    patientDetails,
    "patientId, appointmentId"
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const onSubmit = (data) => {
    const formattedData = formatData(data);
    if (!formattedData || formattedData.length === 0) {
      toast.error("Enter at least one vital sign before submitting");
      return;
    }
    sendData(formattedData, patientId, appointmentId);
  };
  const hasValue = (v) =>
    v !== undefined && v !== null && v !== "" && !Number.isNaN(v);
  const formatData = (data) => {
    const allVitals = [
      {
        name: "Temperature",
        type: "VitalSigns",
        codeDisplay: "Body surface temperature",
        value: data.temperature,
        sign: "F",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "degree Fahrenheit",
        unitCode: "[degF]",
        lowerLimit: null,
        id: "body-temperature",
        upperLimit: null,
      },
      {
        name: "Pulse",
        type: "VitalSigns",
        value: data.pulse,
        codeDisplay: "Heart rate",
        sign: "bpm",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "beats/minute",
        unitCode: "/min",
        lowerLimit: null,
        id: "heart-rate",
        upperLimit: null,
      },
      {
        name: "Respiratory Rate",
        type: "VitalSigns",
        codeDisplay: "Respiratory rate",
        value: data.respiratoryRate,
        sign: "rpm",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "breaths/minute",
        unitCode: "/min",
        lowerLimit: null,
        id: "respiratory-rate",
        upperLimit: null,
      },
      {
        name: "BP",
        type: "VitalSigns",
        codeDisplay: "Blood pressure panel with all children optional",
        value: data.bloodPressure,
        sign: "mmHg",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "mmHg",
        unitCode: "mmHg",
        lowerLimit: "120/80",
        id: "blood-pressure",
        upperLimit: "140/90",
      },
      {
        name: "SPO2",
        type: "VitalSigns",
        codeDisplay: "Oxygen saturation in Arterial blood",
        value: data.spo2,
        sign: "%",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "%",
        unitCode: "%",
        lowerLimit: "90",
        id: "oxygen-saturation",
        upperLimit: "99",
      },
      {
        name: "Height",
        type: "BodyMeasurement",
        codeDisplay: "Body height",
        value: data.height,
        sign: "cm",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "cm",
        unitCode: "cm",
        lowerLimit: null,
        id: "body-height",
        upperLimit: null,
      },
      {
        name: "Weight",
        type: "BodyMeasurement",
        codeDisplay: "Body weight",
        value: data.weight,
        sign: "kg",
        added: true,
        categoryDisplay: "Vital Signs",
        unit: "kg",
        unitCode: "kg",
        lowerLimit: null,
        id: "body-weight",
        upperLimit: null,
      },
    ];
    return allVitals.filter((vital) => hasValue(vital.value));
  };

  const extractErrorMessage = (error) => {
    const data = error?.response?.data;
    let raw = "";
    if (typeof data === "string") {
      raw = data;
    } else if (data && typeof data === "object") {
      raw = data.message || data.detail || data.error || JSON.stringify(data);
    } else {
      raw = error?.message || "";
    }
    if (typeof raw === "string" && raw.toLowerCase().includes("numeric field overflow")) {
      return "A vitals value is out of allowable range (values must be less than 1000 with at most 1 decimal place).";
    }
    return raw || "Failed to save vitals records.";
  };

  const sendData = (formattedData, patientId, appointmentId) => {
    console.log(
      formattedData,
      patientId,
      appointmentId,
      "formattedData, patientId, appointmentId"
    );
    const dateSource = appointmentDate ? new Date(appointmentDate) : new Date();
    const visitDate = `${dateSource.getFullYear()}-${String(
      dateSource.getMonth() + 1
    ).padStart(2, "0")}-${String(dateSource.getDate()).padStart(2, "0")}`;
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}save_wellness_records/`,
        {
          // wellness_data: formattedData,
          // patient_id: "PAT_109",appointment_id:"appointment-test"
          wellness_data: formattedData,
          patient_id: patientId,
          appointment_id: appointmentId,
          visit_date: visitDate,
        },
        {
          headers: headers,
        }
      )
      .then((response) => {
        console.log("Success:", response.data);
        if (response.data?.message === "Successfully uploaded !!") {
          toast.success(
            `${patientDetails.name}’s Vitals/Wellness has been successfully captured`
          );
          handleClose();
        } else {
          const raw =
            response.data?.message ||
            response.data?.error ||
            response.data?.detail ||
            "Something went wrong";
          if (
            typeof raw === "string" &&
            raw.toLowerCase().includes("numeric field overflow")
          ) {
            toast.error(
              "A vitals value is out of allowable range (values must be less than 1000 with at most 1 decimal place)."
            );
          } else {
            toast.error(raw);
          }
        }
      })
      .catch((error) => {
        console.error("Error:", error?.response?.data || error);
        toast.error(extractErrorMessage(error));
      });
  };

  const bpField = register("bloodPressure", {
    validate: (value) => {
      if (!value) return true;
      if (!/^[0-9]{1,3}\/[0-9]{1,3}$/.test(value)) {
        return "Blood Pressure must be in the format xxx/xxx";
      }
      const [systolic, diastolic] = value.split("/").map(Number);
      if (
        isNaN(systolic) ||
        isNaN(diastolic) ||
        systolic < 0 ||
        systolic > 300 ||
        diastolic < 0 ||
        diastolic > 300
      ) {
        return "Each value must be between 0 and 300";
      }
      return true;
    },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "15px",
        padding: "10px",
      }}
    >
      <div
        className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12"
        style={{ margin: "auto" }}
      >
        {/* <div className="contact-panel mb-50" style={{ position: "relative" }}> */}
        {/* <IconButton
            onClick={handleClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 10,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              padding: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
          {/* <h1
            style={{
              textAlign: "center",
              marginBottom: "20px",
              fontSize: "clamp(1.5rem, 4vw, 2rem)",
              paddingTop: "10px",
            }}
          >
            Add New Vitals/Wellness
          </h1> */}
        <form className="contact-panel__form" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="temperature" className="">
                  Temperature (Fahrenheit){" "}
                </label>
                <input
                  type="number"
                  step="0.1"
                  onWheel={(e) => e.target.blur()} // Prevent scroll wheel
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault(); // Prevent arrow keys
                    }
                  }}
                  {...register("temperature", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : parseFloat(v),
                    validate: (value) => {
                      if (value === null || value === undefined) return true;
                      if (Number.isNaN(value)) return true;
                      if (value < 90)
                        return "Temperature must be greater than or equal to 90°F";
                      if (value > 110)
                        return "Temperature must be less than or equal to 110°F";
                      const str = String(value);
                      if (str.includes(".") && str.split(".")[1].length > 1) {
                        return "Temperature takes at most 1 decimal place";
                      }
                      return true;
                    },
                  })}
                  className="form-control"
                  placeholder="Temperature"
                  style={{ fontSize: "16px" }}
                />
                {errors.temperature && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.temperature.message}
                  </p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="pulse" className="">
                  Pulse (beats/minute){" "}
                </label>
                <input
                  type="number"
                  step="1"
                  onWheel={(e) => e.target.blur()} // Prevent scroll wheel
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault(); // Prevent arrow keys
                    }
                  }}
                  {...register("pulse", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : parseFloat(v),
                    validate: (value) => {
                      if (value === null || value === undefined) return true;
                      if (Number.isNaN(value)) return true;
                      if (value < 0)
                        return "Pulse must be greater than or equal to 0";
                      if (value > 250)
                        return "Pulse must be less than or equal to 250";
                      if (!Number.isInteger(Number(value))) {
                        return "Pulse must be a whole number";
                      }
                      return true;
                    },
                  })}
                  className="form-control"
                  placeholder="Pulse"
                  style={{ fontSize: "16px" }}
                />
                {errors.pulse && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.pulse.message}
                  </p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="respiratoryRate" className="">
                  Respiratory Rate (breaths/minute){" "}
                </label>
                <input
                  type="number"
                  step="1"
                  onWheel={(e) => e.target.blur()} // Prevent scroll wheel
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault(); // Prevent arrow keys
                    }
                  }}
                  {...register("respiratoryRate", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : parseFloat(v),
                    validate: (value) => {
                      if (value === null || value === undefined) return true;
                      if (Number.isNaN(value)) return true;
                      if (value < 0)
                        return "Respiratory Rate must be greater than or equal to 0";
                      if (value > 70)
                        return "Respiratory Rate must be less than or equal to 70";
                      if (!Number.isInteger(Number(value))) {
                        return "Respiratory Rate must be a whole number";
                      }
                      return true;
                    },
                  })}
                  className="form-control"
                  placeholder="Respiratory Rate"
                  style={{ fontSize: "16px" }}
                />
                {errors.respiratoryRate && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.respiratoryRate.message}
                  </p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="bloodPressure" className="">
                  Blood Pressure (mmHg){" "}
                </label>
                <input
                  type="text"
                  {...bpField}
                  onChange={(e) => {
                    e.target.value = formatBpWhileTyping(e.target.value);
                    bpField.onChange(e);
                  }}
                  className="form-control"
                  placeholder="Blood Pressure"
                  style={{ fontSize: "16px" }}
                />

                {errors.bloodPressure && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.bloodPressure.message}
                  </p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="spo2" className="">
                  SPO2 (%){" "}
                </label>
                <input
                  type="number"
                  step="1"
                  onWheel={(e) => e.target.blur()} // Prevent scroll wheel
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault(); // Prevent arrow keys
                    }
                  }}
                  {...register("spo2", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : parseFloat(v),
                    validate: (value) => {
                      if (value === null || value === undefined) return true;
                      if (Number.isNaN(value)) return true;
                      if (value < 0)
                        return "SPO2 must be greater than or equal to 0";
                      if (value > 100)
                        return "SPO2 must be less than or equal to 100";
                      if (!Number.isInteger(Number(value))) {
                        return "SPO2 must be a whole number";
                      }
                      return true;
                    },
                  })}
                  className="form-control"
                  placeholder="SPO2"
                  style={{ fontSize: "16px" }}
                />
                {errors.spo2 && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.spo2.message}
                  </p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="height" className="">
                  Height (cm){" "}
                </label>
                <input
                  type="number"
                  step="0.1"
                  onWheel={(e) => e.target.blur()} // Prevent scroll wheel
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault(); // Prevent arrow keys
                    }
                  }}
                  {...register("height", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : parseFloat(v),
                    validate: (value) => {
                      if (value === null || value === undefined) return true;
                      if (Number.isNaN(value)) return true;
                      if (value < 20)
                        return "Height must be greater than or equal to 20";
                      if (value > 220)
                        return "Height must be less than or equal to 220";
                      const str = String(value);
                      if (str.includes(".") && str.split(".")[1].length > 1) {
                        return "Height takes at most 1 decimal place";
                      }
                      return true;
                    },
                  })}
                  className="form-control"
                  placeholder="Height"
                  style={{ fontSize: "16px" }}
                />
                {errors.height && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.height.message}
                  </p>
                )}
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-6 col-lg-6">
              <div className="form-group">
                <label htmlFor="weight" className="">
                  Weight (kg){" "}
                </label>
                <input
                  type="number"
                  step="0.01"
                  onWheel={(e) => e.target.blur()} // Prevent scroll wheel
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault(); // Prevent arrow keys
                    }
                  }}
                  {...register("weight", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : parseFloat(v),
                    validate: (value) => {
                      if (value === null || value === undefined) return true;
                      if (Number.isNaN(value)) return true;
                      if (value < 1)
                        return "Weight must be greater than or equal to 1";
                      if (value > 300)
                        return "Weight must be less than or equal to 300";
                      const str = String(value);
                      if (str.includes(".") && str.split(".")[1].length > 2) {
                        return "Weight takes at most 2 decimal places";
                      }
                      return true;
                    },
                  })}
                  className="form-control"
                  placeholder="Weight"
                  style={{ fontSize: "16px" }}
                />
                {errors.weight && (
                  <p
                    className="errorMsg"
                    style={{ color: "red", fontSize: "14px" }}
                  >
                    {errors.weight.message}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <button
                type="submit"
                className="btn btn__secondary btn__rounded btn__block mt-6"
                style={{
                  width: "100%",
                  maxWidth: "300px",
                  margin: "20px auto",
                  display: "block",
                  padding: "12px 24px",
                  fontSize: "16px",
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    // </div>
  );
};

export default WellmessVitals;
